import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';

import { useShortLocale } from '#configuration/lang/useLocale.ts';
import { getVehicleDisplayName, type Vehicle } from '#domain/Vehicle.ts';
import { hasMissingPocketDriverDataSource } from '#domain/VehicleWarning.ts';

import { useMissingPocketDriverDataSourceBannerState } from './MissingPocketDriverDataSourceBannerState.tsx';

const getAssetAdministrationLink = (assetId: string) =>
    `https://administration.assets.rio.cloud/#/assets/${encodeURIComponent(assetId)}?status=all&tab=device`;

const getTruckLoginManualLink = (locale: string) =>
    `https://rio.cloud/${locale}/documents/manuals/creation-of-a-pocket-driver-device-login/existing-vehicle`;

const MANUAL_LINK_STYLE = {
    alignItems: 'center',
    display: 'inline-flex',
    verticalAlign: 'baseline',
} as const;

const MANUAL_LINK_ICON_STYLE = {
    lineHeight: 1,
} as const;

type MissingPocketDriverDataSourceBannerProps = {
    vehicles: Vehicle[];
};

export const MissingPocketDriverDataSourceBanner = ({ vehicles }: MissingPocketDriverDataSourceBannerProps) => {
    const intl = useIntl();
    const locale = useShortLocale();
    const { dismiss, isDismissed } = useMissingPocketDriverDataSourceBannerState();
    const affectedVehicles = vehicles
        .filter(hasMissingPocketDriverDataSource)
        .slice()
        .sort((a, b) => getVehicleDisplayName(a).localeCompare(getVehicleDisplayName(b)));

    if (isDismissed || affectedVehicles.length === 0) {
        return null;
    }

    return (
        <div
            className='alert alert-dismissible alert-warning margin-bottom-15 padding-top-10 padding-bottom-10'
            role='alert'
            data-testid='missing-pd-data-source-banner'
        >
            <button
                type='button'
                className='btn btn-icon-only close'
                data-dismiss='alert'
                aria-label='Dismiss alert'
                onClick={dismiss}
            >
                <span className='rioglyph rioglyph-remove' aria-hidden='true' />
            </button>
            <div className='display-flex gap-10'>
                <span
                    className='text-color-warning text-size-h4 rioglyph rioglyph-exclamation-sign'
                    aria-hidden='true'
                />
                <div className='min-width-0'>
                    <strong className='text-size-16'>
                        <FormattedPlural
                            value={affectedVehicles.length}
                            one={
                                <FormattedMessage id='intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.title.one' />
                            }
                            other={
                                <FormattedMessage
                                    id='intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.title.other'
                                    values={{ count: affectedVehicles.length }}
                                />
                            }
                        />
                    </strong>
                    <div className='margin-y-5'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.message',
                        })}{' '}
                        <a
                            href={getTruckLoginManualLink(locale)}
                            target='_blank'
                            rel='noreferrer'
                            style={MANUAL_LINK_STYLE}
                            {...getTrackingAttributes({
                                trigger: 'click',
                                category: 'link autoTracking',
                                action: 'link click',
                                label: 'intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.manual-link',
                            })}
                        >
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.manual-link',
                            })}
                            <span
                                className='rioglyph rioglyph-new-window text-size-12 margin-left-3'
                                aria-hidden='true'
                                style={MANUAL_LINK_ICON_STYLE}
                            />
                        </a>
                    </div>
                    <div className='text-size-12'>
                        <span className='text-medium margin-right-5'>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.vehicle.missing-pd-data-source.banner.expander-title',
                            })}
                            :
                        </span>
                        {affectedVehicles.map((vehicle, index) => {
                            const displayName = getVehicleDisplayName(vehicle);
                            return (
                                <span key={vehicle.id} className='white-space-nowrap'>
                                    {index > 0 && <span className='margin-left-5 margin-right-5'>·</span>}
                                    <a href={getAssetAdministrationLink(vehicle.id)} target='_blank' rel='noreferrer'>
                                        {displayName}
                                    </a>
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
